"use client";
import { useComposite } from "../composite/composite.js";
import { TabScopedContextProvider, useTabProviderContext } from "./tab-context.js";
import { createElement, createHook, forwardRef, useWrapElement } from "@ariakit/react-utils";
import { invariant } from "@ariakit/utils";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/tab/tab-list.tsx
const TagName = "div";
/**
* Returns props to create a `TabList` component.
* @see https://ariakit.com/components/tab
* @example
* ```jsx
* const store = useTabStore();
* const props = useTabList({ store });
* <Role {...props}>
*   <Tab>Tab 1</Tab>
*   <Tab>Tab 2</Tab>
* </Role>
* <TabPanel store={store}>Panel 1</TabPanel>
* <TabPanel store={store}>Panel 2</TabPanel>
* ```
*/
const useTabList = createHook(function useTabList({ store, ...props }) {
	const context = useTabProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "TabList must receive a `store` prop or be wrapped in a TabProvider component.");
	const orientation = useStoreState(store, (state) => state.orientation === "both" ? void 0 : state.orientation);
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(TabScopedContextProvider, {
		value: store,
		children: element
	}), [store]);
	if (store.composite) props = {
		focusable: false,
		...props
	};
	props = {
		role: "tablist",
		"aria-orientation": orientation,
		...props
	};
	props = useComposite({
		store,
		...props
	});
	return props;
});
/**
* Renders a composite tab list wrapper for
* [`Tab`](https://ariakit.com/reference/tab) elements.
* @see https://ariakit.com/components/tab
* @example
* ```jsx {2-5}
* <TabProvider>
*   <TabList>
*     <Tab>Tab 1</Tab>
*     <Tab>Tab 2</Tab>
*   </TabList>
*   <TabPanel>Panel 1</TabPanel>
*   <TabPanel>Panel 2</TabPanel>
* </TabProvider>
* ```
*/
const TabList = forwardRef(function TabList(props) {
	return createElement(TagName, useTabList(props));
});
//#endregion
export { TabList, useTabList };

//# sourceMappingURL=tab-list.js.map